import React, { useState } from 'react';
import { CreditCard, CheckCircle2, AlertTriangle, FlaskConical } from 'lucide-react';
import { STATEMENT, PROPOSED_FILE, matchCharge } from '@/lib/chargeMatch';
import { ChargeResult } from '@/components/spec/demo/DemoChargeResult';

function StatementRow({ c, active, onPick }) {
  return (
    <li className={`flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border px-4 py-3 text-sm transition-colors ${active ? 'border-amber-400/60 bg-amber-500/[0.06]' : 'border-white/10 bg-black/30'}`} data-testid={`charge-row-${c.id}`}>
      <span className="font-mono text-xs text-gray-500">{c.date}</span>
      <span className="font-mono text-white" data-testid={`charge-row-${c.id}-name`}>{c.name}</span>
      <span className="font-mono text-gray-200 ml-auto">${c.amount}</span>
      {c.familiar ? <span className="text-xs text-gray-500 w-36 text-right">Recognized</span> : (
        <button type="button" onClick={() => onPick(c)} className="w-36 rounded-full border border-amber-400/50 px-3 py-1 text-xs font-semibold text-amber-200 hover:bg-amber-500/10 transition-colors" data-testid={`charge-row-${c.id}-btn`}>I don't recognize this</button>
      )}
    </li>
  );
}

export function DemoChargeMatch({ onUseOrder }) {
  const [picked, setPicked] = useState(null);
  const result = picked && matchCharge(picked);
  return (
    <section className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.03] p-6 mb-6" data-testid="demo-charge-match">
      <h2 className="text-base md:text-lg font-semibold mb-1 flex items-center gap-2"><CreditCard className="w-4 h-4 text-amber-300" /> Start here. A charge you don't recognize</h2>
      <p className="text-sm text-gray-400 mb-4 max-w-3xl">Your agent picked the store, so the name on your card statement may mean nothing to you. Before calling it fraud, ask your agent to match the charge against the orders it placed.</p>
      <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Pretend card statement</div>
      <ul className="space-y-2 mb-4" data-testid="charge-statement">
        {STATEMENT.map((c) => <StatementRow key={c.id} c={c} active={picked?.id === c.id} onPick={setPicked} />)}
      </ul>
      <div className="rounded-xl border border-violet-400/30 bg-violet-400/[0.04] p-3 flex gap-3 mb-4" data-testid="charge-proposed-notice">
        <FlaskConical className="w-4 h-4 text-violet-300 shrink-0 mt-0.5" />
        <div className="text-xs text-violet-100/80 leading-relaxed min-w-0">
          <span className="font-semibold">Not in Draft 0.4. Proposed for comment.</span> An optional <span className="font-mono">statementNames</span> list in the store's claims file tells agents which card statement names belong to the store.
          <pre className="font-mono text-[11px] text-gray-300 mt-2 whitespace-pre-wrap break-all" data-testid="charge-proposed-file">{JSON.stringify(PROPOSED_FILE)}</pre>
        </div>
      </div>
      {result && (
        <div className="grid lg:grid-cols-2 gap-4" data-testid="charge-result">
          <ul className="space-y-2">
            {result.checks.map((k, i) => {
              const Icon = k.ok ? CheckCircle2 : AlertTriangle;
              return <li key={i} className="flex gap-3 text-sm text-gray-300" data-testid={`charge-check-${i}`}><Icon className={`w-4 h-4 mt-0.5 shrink-0 ${k.ok ? 'text-emerald-400' : 'text-amber-400'}`} />{k.label}</li>;
            })}
          </ul>
          <ChargeResult charge={picked} order={result.order} onUseOrder={onUseOrder} />
        </div>
      )}
    </section>
  );
}
